import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { route } from 'preact-router';

import fields from '../../lib/fields';
import FormFieldsCompact from '../form/fieldsCompact';

const VISIBLE_FIELDS = [
    'callerFirstname',
    'callerLastname',
    'callerCellphone',
    'date',
    'chiefComplaint',
    'advice',
    'outcome',
    'durationMinutes',
    'followUpNeeded',
    'followUpDate',
    'followUpDone',
    'comment',
];

@observer
class PhoneConsultationEdit extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('phoneConsultation'),
            visitorQuery: '',
            visitorResults: [],
            visitorHighlight: 0,
            animalOptions: [],
            saving: false,
            isNew: true,
        };
        this.searchTimer = null;
    }

    componentDidMount() {
        const { stores, id } = this.props;
        const { phoneConsultationStore, userStore } = stores;

        if (id) {
            this.setState({ isNew: false });
            phoneConsultationStore.load(id);
        } else {
            const veterinary = userStore?.user?.id || 0;
            const customer = userStore?.user?.customer || 0;
            phoneConsultationStore.updateKeyValue('newPhoneConsultation', {
                date: new Date().toISOString().slice(0, 16),
                veterinary,
                customer,
            });
            this.focusTimer = setTimeout(() => {
                if (this.visitorInput) this.visitorInput.focus();
            }, 350);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.searchTimer);
        clearTimeout(this.focusTimer);
        this.notifyCallback();
    }

    handleVisitorSearch = (e) => {
        const q = e.target.value;
        this.setState({ visitorQuery: q, visitorHighlight: 0 });
        clearTimeout(this.searchTimer);
        if (!q || q.length < 2) {
            this.setState({ visitorResults: [] });
            return;
        }
        this.searchTimer = setTimeout(async () => {
            const response = await util.fetchApi('/api/visitors/', { method: 'GET' }, { search: q, limit: 8 });
            const visitors = Array.isArray(response?.data) ? response.data : (response?.data?.visitors || []);
            this.setState({ visitorResults: visitors, visitorHighlight: 0 });
        }, 300);
    };

    handleVisitorKeyDown = (e) => {
        const { visitorResults, visitorHighlight } = this.state;
        if (!visitorResults.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState({ visitorHighlight: (visitorHighlight + 1) % visitorResults.length });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState({
                visitorHighlight: (visitorHighlight - 1 + visitorResults.length) % visitorResults.length,
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const visitor = visitorResults[visitorHighlight];
            if (visitor) this.selectVisitor(visitor);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.setState({ visitorResults: [], visitorHighlight: 0 });
        }
    };

    selectVisitor = async (visitor) => {
        const { stores } = this.props;
        const { phoneConsultationStore } = stores;
        const { isNew } = this.state;
        const objectName = isNew ? 'newPhoneConsultation' : 'phoneConsultation';
        const current = phoneConsultationStore[objectName] || {};

        phoneConsultationStore.updateKeyValue(objectName, {
            ...current,
            visitor: visitor.id,
            visitorName: `${visitor.firstname || ''} ${visitor.lastname || ''}`.trim(),
            visitorCellphone: visitor.cellphone || '',
            callerFirstname: visitor.firstname || '',
            callerLastname: visitor.lastname || '',
            callerCellphone: visitor.cellphone || '',
        });

        this.setState({ visitorQuery: '', visitorResults: [] });

        const animalsResponse = await util.fetchApi('/api/animals/', { method: 'GET' }, { owner: visitor.id, limit: 50 });
        const animals = Array.isArray(animalsResponse?.data) ? animalsResponse.data : (animalsResponse?.data?.animals || []);
        this.setState({ animalOptions: animals });
    };

    selectAnimal = (animalId) => {
        const { stores } = this.props;
        const { phoneConsultationStore } = stores;
        const { isNew, animalOptions } = this.state;
        const objectName = isNew ? 'newPhoneConsultation' : 'phoneConsultation';
        const current = phoneConsultationStore[objectName] || {};
        const animal = animalOptions.find(a => a.id === Number(animalId));

        phoneConsultationStore.updateKeyValue(objectName, {
            ...current,
            animal: animal ? animal.id : 0,
            animalName: animal ? (animal.name || '') : '',
            animalSpecies: animal ? (animal.species || '') : '',
        });
    };

    createConsultation = async () => {
        const { stores } = this.props;
        const { phoneConsultationStore } = stores;
        const { newPhoneConsultation } = phoneConsultationStore;
        this.setState({ saving: true });
        const response = await phoneConsultationStore.insert(newPhoneConsultation);
        const created = response?.data?.phoneConsultation;
        if (created && created.id) {
            phoneConsultationStore.updateKeyValue('phoneConsultation', created);
            phoneConsultationStore.updateKeyValue('newPhoneConsultation', {});
            this.setState({ isNew: false, saving: false });
        } else {
            this.setState({ saving: false });
        }
    };

    notifyCallback = () => {
        const { callback, stores } = this.props;
        const { phoneConsultationStore } = stores;
        if (typeof callback === 'function') {
            const current = phoneConsultationStore.phoneConsultation
                || phoneConsultationStore.newPhoneConsultation
                || null;
            callback(current && current.id ? current : null);
        }
    };

    escalateToSale = async () => {
        const { stores } = this.props;
        const { phoneConsultationStore, appState } = stores;
        const { isNew } = this.state;

        if (isNew) {
            await this.createConsultation();
        }
        const current = phoneConsultationStore.phoneConsultation;
        if (!current || !current.id) return;

        const result = await phoneConsultationStore.escalateToSale(current.id);
        if (result?.saleId) {
            this.notifyCallback();
            appState.toggleDrawer(false);
            route(`/dyrejournal-sale/${result.saleId}`);
        }
    };

    escalateToJournal = async () => {
        const { stores } = this.props;
        const { phoneConsultationStore, appState } = stores;
        const { isNew } = this.state;

        if (isNew) {
            await this.createConsultation();
        }
        const current = phoneConsultationStore.phoneConsultation;
        if (!current || !current.id) return;

        const result = await phoneConsultationStore.escalateToJournal(current.id);
        if (result?.journalId) {
            this.notifyCallback();
            appState.toggleDrawer(false);
        }
    };

    close = () => {
        const { stores } = this.props;
        const { appState } = stores;
        this.notifyCallback();
        appState.toggleDrawer(false);
    };

    render() {
        const { sections, visitorQuery, visitorResults, visitorHighlight, animalOptions, saving, isNew } = this.state;
        const { stores } = this.props;
        const { phoneConsultationStore } = stores;
        const currentObject = isNew
            ? phoneConsultationStore.newPhoneConsultation
            : phoneConsultationStore.phoneConsultation;

        return (
            <div class='container-fluid p-3' style='padding-top: 60px !important;'>
                <h4 class='mb-3'>
                    <i class='fa-duotone fa-phone-volume me-2' />
                    <Text id='phoneConsultation.title'>Telefonkonsultasjon</Text>
                </h4>

                {isNew && (
                    <div class='mb-3'>
                        <label class='form-label'>
                            <Text id='phoneConsultation.findCaller'>Søk etter eksisterende kunde</Text>
                        </label>
                        <input
                            ref={el => { this.visitorInput = el; }}
                            type='text'
                            class='form-control'
                            placeholder='Navn eller telefon'
                            value={visitorQuery}
                            onInput={this.handleVisitorSearch}
                            onKeyDown={this.handleVisitorKeyDown}
                        />
                        {visitorResults.length > 0 && (
                            <div class='position-relative'>
                                <div class='position-absolute list-group shadow w-100' style='z-index: 10;'>
                                    {visitorResults.map((v, idx) => (
                                        <button
                                            key={v.id}
                                            type='button'
                                            class={`list-group-item list-group-item-action${idx === visitorHighlight ? ' active' : ''}`}
                                            onClick={() => this.selectVisitor(v)}
                                            onMouseEnter={() => this.setState({ visitorHighlight: idx })}
                                        >
                                            <strong>{v.firstname} {v.lastname}</strong>
                                            {' '}
                                            <small class={idx === visitorHighlight ? '' : 'text-body-secondary'}>{v.cellphone}</small>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {currentObject?.visitor > 0 && animalOptions.length > 0 && (
                    <div class='mb-3'>
                        <label class='form-label'>
                            <Text id='phoneConsultation.selectAnimal'>Velg pasient</Text>
                        </label>
                        <select
                            class='form-select'
                            value={currentObject.animal || ''}
                            onChange={(e) => this.selectAnimal(e.target.value)}
                        >
                            <option value=''>—</option>
                            {animalOptions.map(a => (
                                <option key={a.id} value={a.id}>{a.name} ({a.species})</option>
                            ))}
                        </select>
                    </div>
                )}

                <FormFieldsCompact
                    stores={stores}
                    sections={sections}
                    fields={VISIBLE_FIELDS}
                    holdingStore={phoneConsultationStore}
                    objectName={isNew ? 'newPhoneConsultation' : 'phoneConsultation'}
                    isNew={isNew}
                />

                <div class='mt-3 d-flex gap-2 flex-wrap align-items-center'>
                    {isNew && (
                        <button type='button' class='btn btn-primary' onClick={this.createConsultation} disabled={saving}>
                            <Text id='phoneConsultation.save'>Lagre</Text>
                        </button>
                    )}
                    {!isNew && (
                        <small class='text-body-secondary'>
                            <Text id='phoneConsultation.autosaved'>Endringer lagres automatisk</Text>
                            {saving && <span class='ms-2'><i class='fa-duotone fa-spinner fa-spin' /></span>}
                        </small>
                    )}
                    <div class='ms-auto d-flex gap-2'>
                        <button type='button' class='btn btn-outline-secondary' onClick={this.escalateToSale} disabled={saving}>
                            <Text id='phoneConsultation.escalateToSale'>Opprett sak</Text>
                        </button>
                        <button type='button' class='btn btn-outline-secondary' onClick={this.escalateToJournal} disabled={saving}>
                            <Text id='phoneConsultation.escalateToJournal'>Opprett full journal</Text>
                        </button>
                        <button type='button' class='btn btn-secondary' onClick={this.close}>
                            <Text id='phoneConsultation.close'>Lukk</Text>
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default PhoneConsultationEdit;
